import axios from 'axios';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const USER_API_URL = `${BASE_API_URL}/permissionUsers`;

export interface User {
  id: number;
  userName: string;
  email?: string;
  roleId?: number;
  roleName?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserResponse {
  message?: string;
  detail?: string;
}

/**
 * Get all users
 * Calls the GET /api/User/all endpoint
 */
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get<User[]>(`${USER_API_URL}/all`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to get all users:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get all users'
    );
  }
};

/**
 * Get user by ID
 * Calls the GET /api/User/{id} endpoint
 */
export const getUserById = async (id: number): Promise<User> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get<User>(`${USER_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to get user ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get user'
    );
  }
};

/**
 * Create a new user
 * Calls the POST /api/User endpoint
 */
export const createUser = async (userData: Partial<User>): Promise<User> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post<User>(USER_API_URL, userData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to create user:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to create user'
    );
  }
};

/**
 * Update an existing user
 * Calls the PUT /api/User/{id} endpoint
 */
export const updateUser = async (id: number, userData: Partial<User>): Promise<User> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.put<User>(`${USER_API_URL}/${id}`, userData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to update user ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to update user'
    );
  }
};

/**
 * Delete a user
 * Calls the DELETE /api/User/{id} endpoint
 */
export const deleteUser = async (id: number): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${USER_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
  } catch (error: any) {
    console.error(`Failed to delete user ${id}:`, error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to delete user'
    );
  }
};

/**
 * Soft-delete a user by setting isActive = false
 * Calls PUT /api/permissionUsers/{id} with isActive: false
 */
export const deactivateUser = async (id: number): Promise<User> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.put<User>(`${USER_API_URL}/${id}`, { isActive: false }, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to deactivate user ${id}:`, error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to deactivate user'
    );
  }
};

/**
 * Transfer all approval tasks from one user to another.
 * Calls POST /api/UserTaskTransfer
 * Updates QmsStepInstanceApprovers and QmsWorkflowStepApprovers.
 */
export const transferUserTasks = async (
  fromUserId: number,
  toUserId: number,
  comment?: string
): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    await axios.post(
      `${BASE_API_URL}/UserTaskTransfer`,
      { fromUserId, toUserId, comment: comment ?? '' },
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error: any) {
    console.error('Failed to transfer user tasks:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to transfer tasks'
    );
  }
};
